

import { Component, Prop, Watch } from 'vue-property-decorator';
import ErrorMessageHandlerMixin from '../misc/ErrorMessageHandler.mixin';
import { mixins } from 'vue-class-component';
import { required, numeric, requiredUnless } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import { namespace } from 'vuex-class';
import Company from '@/models/Company';
import { applicationStoreGetter } from '@/store/application.store';
import Taxi from '@/models/Taxi';
import { taxiStoreActions } from '@/store/taxi.store';
import { RepositoryFactory } from '@/api/RepositoryFactory';
import UploadRepository from '@/api/repositories/UploadRepository';
import FileRepository from '@/api/repositories/FileRepository';
import { compressImage } from '@/misc/imageCompression';

const TaxiStore = namespace('taxi');
const ApplicationStore = namespace('application');

const uploadRepository: UploadRepository = RepositoryFactory.get('upload');
const fileRepository: FileRepository = RepositoryFactory.get('file');

/**
 * maximum edge length taxi images are compressed to before uploading
 */
const TAXI_IMAGE_MAX_EDGE = 1280;

/**
 * maximum edge length of the automatically generated main thumbnail
 */
const TAXI_THUMB_MAX_EDGE = 200;

/**
 * a pickable taxi image slot (mainThumb is generated automatically from main)
 */
interface TaxiImageSlot {
    key: string,
    file: File | null,
    previewUrl: string
}
@Component({
    mixins: [validationMixin],
    validations: {
        taxiCopy: {
            taxiNo: {
                required: requiredUnless('privateDrive')
            },
            privateDrive: {
                required: requiredUnless('taxiNo')
            },
            plate: {
                required
            },
            seats: {
                required,
                numeric
            },
            type: {
                required
            }
        }
    }
})
export default class EditTaxiComponent extends mixins(ErrorMessageHandlerMixin) {
    @Prop({ default: () => new Taxi() })
    public taxi!: Taxi;

    @ApplicationStore.Getter(applicationStoreGetter.SELECTED_COMPANY)
    public selectedCompany!: Company;

    @TaxiStore.Action(taxiStoreActions.CREATE)
    public createTaxiAction!: (payload: { taxi: Taxi, company: Company }) => Promise<Taxi>;

    @TaxiStore.Action(taxiStoreActions.UPDATE)
    public updateTaxiAction!: (payload: { taxi: Taxi }) => Promise<Taxi>;

    public taxiCopy: Taxi = new Taxi();
    public validData = false;
    public isLoading: boolean = false;

    /**
     * the pickable image slots (mainThumb is generated from main on upload)
     */
    public imageSlots: TaxiImageSlot[] = ['main', 'front', 'right', 'back', 'left', 'backSeat', 'frontSeat']
        .map((key: string) => ({ key, file: null, previewUrl: '' }));

    /**
     * the slot the user is currently picking a file for
     */
    private pendingSlot: TaxiImageSlot | null = null;

    @Watch('taxi', { immediate: true })
    public taxiChanged() {
        if (this.taxi) {
            this.taxiCopy = this.taxi.copy() as Taxi;
            this.validData = !this.$v!.$invalid;
        }
    }

    private async created() {
        await this.loadExistingImages();
    }

    private beforeDestroy() {
        this.imageSlots.forEach((imageSlot) => this.revokeSlotPreview(imageSlot));
    }

    /**
     * Loads the already stored images of the taxi as previews
     */
    private async loadExistingImages() {
        const images = this.taxi?.images;
        if (!images) return;
        await Promise.all(this.imageSlots.map(async (imageSlot) => {
            const fileId = (images as any)[imageSlot.key];
            if (!fileId) return;
            try {
                imageSlot.previewUrl = await fileRepository.getFileObjectUrl(fileId);
            } catch (e) {
                // preview couldn't be loaded, the placeholder is shown instead
            }
        }));
    }

    private revokeSlotPreview(imageSlot: TaxiImageSlot) {
        if (imageSlot.previewUrl) {
            URL.revokeObjectURL(imageSlot.previewUrl);
            imageSlot.previewUrl = '';
        }
    }

    public onPickImage(imageSlot: TaxiImageSlot) {
        this.pendingSlot = imageSlot;
        (this.$refs.imageInput as HTMLInputElement).click();
    }

    public onImageSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files && input.files.length > 0 ? input.files[0] : null;
        // reset the input so choosing the same file again triggers a change event
        input.value = '';
        const imageSlot = this.pendingSlot;
        this.pendingSlot = null;
        if (!imageSlot || !file || !file.type.startsWith('image/')) return;
        this.revokeSlotPreview(imageSlot);
        imageSlot.file = file;
        imageSlot.previewUrl = URL.createObjectURL(file);
    }

    /**
     * Compresses and uploads all newly selected images for the given taxi.
     * The main image additionally generates a small thumbnail (mainThumb).
     * Called after the taxi record itself was created/saved.
     */
    private async uploadImages(taxiId: string) {
        if (!taxiId) return;
        let failed = false;
        for (const imageSlot of this.imageSlots) {
            if (!imageSlot.file) continue;
            try {
                const image = await compressImage(imageSlot.file, TAXI_IMAGE_MAX_EDGE);
                await uploadRepository.uploadTaxiImage({ id: taxiId, slot: imageSlot.key, image });
                if (imageSlot.key === 'main') {
                    const thumb = await compressImage(imageSlot.file, TAXI_THUMB_MAX_EDGE);
                    await uploadRepository.uploadTaxiImage({ id: taxiId, slot: 'mainThumb', image: thumb });
                }
                imageSlot.file = null;
            } catch (e) {
                failed = true;
            }
        }
        if (failed) {
            this.$notifyErrorSimplified('TAXI.NOTIFICATIONS.IMAGE_UPLOAD_ERROR');
        }
    }

    public async saveData() {
        if (this.validData) {
            try {
                this.isLoading = true;
                let savedTaxi: Taxi;
                if (!this.taxi.id) {
                    savedTaxi = await this.createTaxiAction({
                        taxi: this.taxiCopy,
                        company: this.selectedCompany
                    });
                } else {
                    savedTaxi = await this.updateTaxiAction({ taxi: this.taxiCopy });
                }
                await this.uploadImages(savedTaxi?.id ?? this.taxi.id);
                this.dismiss(true);
            } catch (err) {
                console.log(err);
                const knownErrors = [
                    'TAXI_NOT_FOUND',
                    'TAXI_NO_PERMIT_NO_COMBINATION_ALREADY_EXISTS',
                    'TAXI_PLATE_ALREADY_IN_COMPANY'
                ];
                if (err && knownErrors.includes(err.message)) {
                    this.$notifyErrorSimplified(`TAXI.NOTIFICATIONS.${err.message}`);
                } else {
                    this.$notifyErrorSimplified('GENERAL.NOTIFICATIONS.SERVER_ERROR');
                }
            } finally {
                this.isLoading = false;
            }
        }
    }

    public dismiss(reloadData: boolean = false) {
        if (reloadData) {
            this.taxiCopy = new Taxi();
        }

        this.$v.$reset();
        this.$emit('closeDialog', reloadData);
    }

    public checkForm(type: string) {
        this.validData = !this.$v!.$invalid;
        this.triggerValidation(type);
    }
}

